import type { TranslationKey, TranslationVariables } from "@/lib/translations"
import type { AtsExplorerFilter, AtsFilterDefinition } from "@/types/ats-explorer-api"

/** The active language's translator, passed in because this module has no hooks */
type Translate = (key: TranslationKey, variables?: TranslationVariables) => string

export const DEFAULT_OPERATOR = "eq"
export const ANY_VALUE = "__any__"

/**
 * Vendors clamp large reference sets (Bullhorn options at 300). A set that comes back
 * at the clamp is treated as incomplete and searched server-side as the user types.
 */
export const REFERENCE_TYPEAHEAD_THRESHOLD = 300

/** Operator → string key; the label itself is translated where the operator is shown */
export const OPERATOR_LABEL_KEYS: Record<string, TranslationKey> = {
  eq: "explorer.filters.operators.eq",
  ne: "explorer.filters.operators.ne",
  gt: "explorer.filters.operators.gt",
  lt: "explorer.filters.operators.lt",
  gte: "explorer.filters.operators.gte",
  lte: "explorer.filters.operators.lte",
  contains: "explorer.filters.operators.contains",
  startsWith: "explorer.filters.operators.startsWith",
  isNull: "explorer.filters.operators.isNull",
  isNotNull: "explorer.filters.operators.isNotNull",
  isEmpty: "explorer.filters.operators.isEmpty",
  isNotEmpty: "explorer.filters.operators.isNotEmpty",
  memberOf: "explorer.filters.operators.memberOf",
  notMemberOf: "explorer.filters.operators.notMemberOf",
}

/** An operator's label, falling back to the operator itself for one the vendor names and we do not */
export function operatorLabel(operator: string, t: Translate): string {
  const key = OPERATOR_LABEL_KEYS[operator]
  return key ? t(key) : operator
}

/** Operators that need no value (null and emptiness checks) */
const VALUELESS_OPERATORS = new Set(["isNull", "isNotNull", "isEmpty", "isNotEmpty"])

export function isValuelessOperator(operator: string): boolean {
  return VALUELESS_OPERATORS.has(operator)
}

/** One filter's state: the chosen operator, its value(s), and display labels captured at pick time */
export interface FilterValueState {
  operator: string
  values: string[]
  /** Reference value id → name, remembered so a chip never degrades to the raw id */
  valueLabels?: Record<string, string>
}

/** Filter state keyed by filter name; multi-value filters hold several entries */
export type FilterValues = Record<string, FilterValueState>

/** The chosen operator, else the filter's first supported one (to-many filters have no "is") */
export function defaultOperatorFor(definition: AtsFilterDefinition): string {
  return definition.supportedOperators?.[0] ?? DEFAULT_OPERATOR
}

export function operatorOf(values: FilterValues, definition: AtsFilterDefinition): string {
  return values[definition.name]?.operator ?? defaultOperatorFor(definition)
}

/** Whether a filter carries anything the vendor will receive */
export function hasFilterState(state: FilterValueState | undefined): boolean {
  if (!state) return false
  return isValuelessOperator(state.operator) || state.values.some((v) => v !== "")
}

export function activeFilterCount(values: FilterValues): number {
  return Object.values(values).filter(hasFilterState).length
}

/**
 * Converts filter values into the API's filter list — one entry per value, or a
 * single empty-valued entry for value-less operators
 */
export function toExplorerFilters(values: FilterValues): AtsExplorerFilter[] {
  return Object.entries(values).flatMap(([field, state]) => {
    const operator = state.operator || DEFAULT_OPERATOR
    if (isValuelessOperator(operator)) {
      return [{ field, operator, value: "" }]
    }
    return state.values
      .filter((value) => value !== "" && value !== ANY_VALUE)
      .map((value) => ({ field, operator, value }))
  })
}

/** The required filters (e.g., parent IDs of nested resources) that have no value yet */
export function getMissingRequiredFilters(definitions: AtsFilterDefinition[], values: FilterValues): AtsFilterDefinition[] {
  return definitions.filter((d) => d.isRequired && !hasFilterState(values[d.name]))
}

/** Returns the next state after replacing a filter's values (labels merged, empties dropped) */
export function withFilterValues(
  values: FilterValues,
  definition: AtsFilterDefinition,
  nextValues: string[],
  labels?: Record<string, string>
): FilterValues {
  const cleaned = nextValues.filter((v) => v !== "" && v !== ANY_VALUE)
  const current = values[definition.name]
  if (cleaned.length === 0 && !current) {
    return values
  }
  // An empty entry survives to keep a chosen operator until the filter is removed;
  // toExplorerFilters and the required-filter check both ignore empty values
  return {
    ...values,
    [definition.name]: {
      operator: current?.operator ?? defaultOperatorFor(definition),
      values: cleaned,
      valueLabels: { ...current?.valueLabels, ...labels },
    },
  }
}

export function withFilterOperator(values: FilterValues, definition: AtsFilterDefinition, operator: string): FilterValues {
  const current = values[definition.name]
  // A value-less operator has nothing to carry over; anything else keeps the values
  const carried = isValuelessOperator(operator) ? [] : (current?.values ?? [])
  return { ...values, [definition.name]: { operator, values: carried, valueLabels: current?.valueLabels } }
}

export function withoutFilter(values: FilterValues, name: string): FilterValues {
  if (!(name in values)) return values
  const next = { ...values }
  delete next[name]
  return next
}

/** ISO-8601 (or empty) → the "YYYY-MM-DDTHH:mm" a datetime-local input shows, in local time */
export function toDateTimeLocal(iso: string | undefined): string {
  if (!iso) return ""
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ""
  const pad = (n: number) => String(n).padStart(2, "0")
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

/** datetime-local value → ISO-8601 UTC ("" stays "") */
export function fromDateTimeLocal(local: string): string {
  if (!local) return ""
  const date = new Date(local)
  return Number.isNaN(date.getTime()) ? "" : date.toISOString()
}

function formatChipValue(definition: AtsFilterDefinition, value: string, labels: Record<string, string> | undefined, t: Translate): string {
  if (definition.dataType === "datetime") {
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
  }
  if (definition.dataType === "boolean") return t((value === "true" ? "explorer.filters.chipValue.yes" : "explorer.filters.chipValue.no"))
  return labels?.[value] ?? value
}

/** "Status is Approved", "Owner includes Jane, John", "Commissions has any" — the chip text */
export function describeFilter(definition: AtsFilterDefinition, state: FilterValueState, t: Translate): string {
  const operator = operatorLabel(state.operator, t)
  if (isValuelessOperator(state.operator)) {
    return `${definition.displayName} ${operator}`
  }
  const shown = state.values.slice(0, 2).map((value) => formatChipValue(definition, value, state.valueLabels, t))
  const more = state.values.length > 2 ? ` +${state.values.length - 2}` : ""
  return `${definition.displayName} ${operator} ${shown.join(", ")}${more}`
}
